import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Lock,
  Mail,
  ArrowRight,
  Loader2,
  Command,
  EyeOff,
  Eye,
} from 'lucide-react'
import axios from 'axios'
import logo from '@/assets/logo.png'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import { BASE_URL } from '@/constant'
export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email || !password) {
      setError('Please fill in all fields')
      return
    }

    setIsLoading(true)
    try {
      const res = await axios.post(`${BASE_URL}/auth/login`, {
        email,
        password,
      })

      Cookies.set('token', res.data.accessToken)
      Cookies.set('refreshToken', res.data.refreshToken, { expires: 7 })
      toast.success('Login successful')
      navigate('/')
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message ?? 'Something went wrong')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='min-h-screen w-full flex bg-white font-sans selection:bg-black selection:text-white'>
      {/* Visual Side (Hidden on Mobile) */}
      <div className='hidden lg:flex w-1/2 bg-black flex-col justify-center gap-10 p-12 text-white relative overflow-hidden'>
        <div className='absolute inset-0 opacity-20 pointer-events-none'>
          <div className='absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-white blur-[120px]' />
          <div className='absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-slate-800 blur-[120px]' />
        </div>

        <div className='relative z-10 flex items-center gap-2 group cursor-default'>
          <div className='w-10 h-10 rounded-lg bg-white flex items-center justify-center transition-transform group-hover:scale-110'>
            <img src={logo} alt='logo' className='w-6 h-6 text-black' />
          </div>
          <span className='text-2xl font-bold tracking-tighter'>
            ClickVoltz<span className='text-[#FFC107]'>.</span>
          </span>
        </div>

        <div className='relative z-10 max-w-md'>
          <h1 className='text-5xl font-extrabold tracking-tight leading-[1.1] mb-6'>
            Management <br />
            <span className='text-slate-400'>Architected for Speed.</span>
          </h1>
          <p className='text-lg text-slate-400 font-medium'>
            Streamline your workflow with our next-gen project intelligence
            platform.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className='w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50/30'>
        <div className='w-full max-w-[400px] animate-in fade-in slide-in-from-bottom-4 duration-1000'>
          <Card className='border-none shadow-none bg-transparent'>
            <CardHeader className='p-0 mb-8 space-y-2'>
              <div className='lg:hidden flex items-center gap-2 mb-8'>
                <Command className='w-8 h-8 text-black' />
                <span className='text-xl font-bold tracking-tighter'>
                  ClickUp
                </span>
              </div>
              <CardTitle className='text-3xl font-bold tracking-tight text-black'>
                Sign in
              </CardTitle>
              <CardDescription className='text-slate-500 font-medium'>
                Access your workspace and projects
              </CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              <form onSubmit={handleLogin} className='space-y-6'>
                <div className='space-y-4'>
                  <div className='space-y-2 group'>
                    <Label
                      htmlFor='email'
                      className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                    >
                      Email Address <span className='text-red-500'>*</span>
                    </Label>
                    <div className='relative'>
                      <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                      <Input
                        id='email'
                        type='email'
                        placeholder='name@company.com'
                        className='pl-10 h-11 bg-white border-slate-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-black text-black transition-all rounded-lg'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className='space-y-2 group'>
                    <div className='flex items-center justify-between'>
                      <Label
                        htmlFor='password'
                        className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                      >
                        Password <span className='text-red-500'>*</span>
                      </Label>
                    </div>

                    <div className='relative'>
                      {/* Left icon */}
                      <Lock className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />

                      {/* Input */}
                      <Input
                        id='password'
                        type={showPassword ? 'text' : 'password'}
                        placeholder='••••••••'
                        className='pl-10 pr-10 h-11 bg-white border-slate-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-black text-black transition-all rounded-lg'
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={isLoading}
                      />

                      {/* Toggle button */}
                      <button
                        type='button'
                        onClick={() => setShowPassword(prev => !prev)}
                        className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black transition-colors'
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <Eye className='w-4 h-4' />
                        ) : (
                          <EyeOff className='w-4 h-4' />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className='p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2 animate-in fade-in zoom-in-95 duration-200'>
                    <div className='w-1 h-1 rounded-full bg-red-600' />
                    <p className='text-xs font-bold text-red-600 uppercase tracking-wider'>
                      {error}
                    </p>
                  </div>
                )}
                <div className='flex justify-end'>
                  <Link
                    to={'/reset-password'}
                    className=' text-black text-xs hover:border-b-[1px] border-black '
                    type='button'
                  >
                    Forgot Password?
                  </Link>
                </div>
                <Button
                  type='submit'
                  className='w-full h-11 bg-black hover:bg-slate-800 text-white font-bold rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 shadow-xl shadow-black/10'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className='w-5 h-5 animate-spin' />
                  ) : (
                    <span className='flex items-center gap-2'>
                      Sign In <ArrowRight className='w-4 h-4' />
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
